<template>
  <button
    v-if="dateRange.length === 1"
    type="button"
    :class="{
      'is-selected': isSelected,
      'button-preset': true,
      'text-body-3': true,
      'is-last': isLastItem,
    }"
    :disabled="dateRange[0].disabled"
    @click="emit('update:model-value', dateRange[0].value)"
  >
    {{ dateRange[0].label }}
  </button>
  <v-select
    v-else
    v-model="selectedValue"
    :items="dateRange"
    item-title="label"
    item-value="value"
    item-props
    :class="{ 'is-selected': isSelected, 'is-last': isLastItem }"
    menu-icon="$chevron-down"
    :menu-props="{
      contentClass: 'date-picker-preset-select',
      offset: 12,
    }"
    :disabled="disableSelect"
  >
    <template #selection="{ item }">
      <button
        type="button"
        :disabled="disableSelect"
        @click="emit('update:model-value', item.value)"
        @mousedown.stop.prevent
      >
        <DatePickerTooltip
          :activator-text="item.title"
          :date-range="item.value"
          :hide-tooltip="disableSelect"
        />
      </button>
    </template>
    <template #item="{ props: itemProps, item, index }">
      <v-list-item
        v-bind="itemProps"
        :disabled="itemProps.disabled"
        :class="{
          'is-first': index === 0,
          'is-last': index === dateRange?.length - 1,
        }"
      >
        <template #title="{ title }">
          <DatePickerTooltip
            :activator-text="title"
            :date-range="item.value"
            :hide-tooltip="itemProps.disabled"
            tooltip-placement="left"
          />
        </template>
      </v-list-item>
    </template>
  </v-select>
</template>

<script setup>
import { computed } from 'vue';
import DatePickerTooltip from '@/components/filter/dateFilters/DatePicker/DatePickerTooltip.vue';

const props = defineProps({
  dateRange: {
    type: Array,
    default: () => ([]),
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  selectedPreset: {
    type: Object,
    default: () => ({}),
  },
  defaultRange: {
    type: Object,
    default: () => ({}),
  },
  isLastItem: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:model-value']);

const selectedValue = computed({
  get: () => {
    if (props.isSelected) return props.selectedPreset;
    if (Object.keys(props.defaultRange)?.length) return props.defaultRange;
    return props.dateRange[0];
  },
  set: (newVal) => {
    emit('update:model-value', newVal);
  },
});

const disableSelect = computed(() => props.dateRange.every((preset) => preset.disabled));
</script>

<style lang="less" scoped>
.button-preset {
  background-color: @bg-default;
  color: @text-dark;
  border-left: 1px solid @stroke-default;
  min-height: 40px;
  padding: 0 12px;

  &.is-last {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }

  &.is-selected, &:hover {
    background-color: @bg-brand-light;
    color: @text-brand;
  }

  &[disabled], &[disabled=true] {
    background-color: @bg-dark;
    color: @text-dark;
    pointer-events: none;
  }
}

.v-select.v-input {
  font-size: 12px;
  font-weight: 500;
  border-left: 1px solid @stroke-default;

  :deep(input), :deep(.v-field) {
    font-size: 12px;
  }

  :deep(.v-select__selection-text), :deep(.v-select__selection) {
    color: @text-dark;
  }

  :deep(.v-field__input) {
    padding-bottom: 0px;
    padding-top: 0px;
    padding-left: 12px;
    padding-right: 0px;
    min-height: 40px;
    height: 40px;
    opacity: 1;
  }

  :deep(.v-field--appended) {
    padding-inline-end: 6px;
  }

  :deep(.v-field__append-inner) svg {
    margin-inline-start: 0px;
  }

  :deep(.v-field__append-inner) > .v-icon {
    opacity: 1;

    path {
      fill: @text-dark;
    }
  }

  &.is-last {
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
  }
}

:deep(.v-field__overlay), :deep(.v-field__outline), :deep(.v-input__details) {
  display: none;
}

.v-select.is-selected,
.v-select.v-input:hover {
  background-color: @bg-brand-light;

  :deep(.v-select__selection-text), :deep(.v-select__selection) {
    color: @text-brand;
  }

  :deep(.v-field__append-inner) svg path {
    fill: @icon-brand;
  }
}

:deep(.v-field--disabled) {
  background-color: @bg-dark;
  color: @text-dark;
  opacity: 1;

  .v-select__selection-text, .v-select__selection {
    color: @text-dark !important;
  }

  .v-field__append-inner svg path {
    fill: @icon-dark !important;
  }
}
</style>

<style lang="less">
.date-picker-preset-select.v-overlay__content > .v-list {
  padding: 0px;
  background-color: @bg-default;
  border: 1px solid @stroke-default;
  border-radius: 8px;
  box-shadow: none;
  min-width: 142px;
  overflow: visible;

  .v-list-item {
    transition: all 300ms ease-in-out;
    border-bottom: 1px solid @stroke-default;

    .v-list-item-title {
      color: @text-dark !important;
      overflow: visible;
    }

    .v-list-item__content {
      overflow: visible;
    }

    &.is-first {
      border-top-left-radius: 6px !important;
      border-top-right-radius: 6px !important;
    }

    &.is-last {
      border-bottom: none;
      border-bottom-left-radius: 6px !important;
      border-bottom-right-radius: 6px !important;
    }
  }

  .v-list-item[aria-selected=true] {
    background-color: @bg-brand-light;

    .v-list-item-title {
      color: @text-brand !important;
    }
  }

  .v-list-item-title {
    font-size: 13px;
    font-weight: 500;
  }

  .v-list-item--density-default.v-list-item--one-line {
    min-height: 36px;
    padding-top: 0px;
    padding-bottom: 0px;
  }

  .v-list-item__overlay {
    display: none;
  }

  .v-list-item:hover {
    background-color: @bg-brand-light;

    .v-list-item-title {
      color: @text-brand !important;
    }
  }

  .select-list-item {
    color: @text-dark !important;
  }
}
</style>
